import React, { useRef, useEffect, useState } from "react";
import defaultClouds from "../assets/cloud.png";
import defaultClouds2 from "../assets/cloud2.png";
import defaultForeground from "../assets/bg3.jpg";
import bajajImg from "../assets/bajaj2.png";
import mountain from "../assets/mountain.PNG";
import tree from "../assets/tree3.PNG";
import wall1 from "../assets/wall1.png";
import wall2 from "../assets/wall2.png";
import building from "../assets/building.png";



/**
 * GameScene component renders a parallax scene with two backgrounds,
 * a character (bajaj.png), and handles movement according to multiplier and game crash. 
 * 
 * Props:
 * - multiplier: current multiplier (number)
 * - speed: base speed of parallax (number, px/sec)
 * - isCrashed: boolean indicating if the game has crashed
 * - customerName (optional): for fun, show customer riding bajaj
 * - crashMultiplier: when reaching this multiplier, triggers "crash" fast-out
 */

const PARALLAX_SPEED_BG1 = 0.3; // bg1 moves slower (distant mountains/clouds)
const PARALLAX_SPEED_BG2 = 1;   // bg2 moves faster (foreground)
const BAJAJ_BASE_Y = 270;
const BAJAJ_CRASH_SPEED = 2000; // px/sec when crash
const BG_IMG_WIDTH = 3600;      // All bg images are 3600px!

function clamp(val, min, max) {
  return Math.min(Math.max(val, min), max);
}


const MovingObject = ({ name, children, speed = 1, initialDirection = -1, startX = 0 , startY = 0 , height = 100 , canReset = true , startOffScreen = true , animate = true }) => { // Added default props
    const containerRef = useRef(null);
    const objectRef = useRef(null);
    const [position, setPosition] = useState(startX);
    const direction = useRef(initialDirection); // Use ref for constant direction during animation
    let containerWidth = 0;
    let objectWidth = 0;
  
    useEffect(() => {
      let animationFrame;
      
      let x = startX;
  
      const updateDimensionsAndInitialize = () => {
        if (containerRef.current && objectRef.current) {
          containerWidth = containerRef.current.offsetWidth;
          objectWidth = objectRef.current.offsetWidth;
  
          // Ensure initial position is completely off-screen

          if (startOffScreen) {
            if (direction.current === -1) { // Moving left
                x = startX + containerWidth; // Start fully to the right of the container
            } else { // Moving right
                x = objectWidth - startX; // Start fully to the left of the container
            }
          }
         else {
            x = startX;
         }
          
          // Set the initial position immediately to render it off-screen first
          setPosition(x); 
        }
      };
  
      const move = () => {
        x += speed * direction.current;

        // Wrap-around logic: when object fully leaves one side, reset its position to the other side
        if (direction.current === -1) { // Moving left
          if (x < -objectWidth) {
            if(!canReset) {
              cancelAnimationFrame(animationFrame);
              return;
            }
            x = containerWidth;
          }
        } else { // Moving right
          if (x > containerWidth) {
            if(!canReset) {
              cancelAnimationFrame(animationFrame);
              return;
            }
            x = -objectWidth;
          }
        }
  
        setPosition(x);

        if (animate) {
            animationFrame = requestAnimationFrame(move);
        }
        
      };
  
      // Initialize dimensions and position immediately on mount
      updateDimensionsAndInitialize();
      if (animate) {
      // Start the animation loop right away
        animationFrame = requestAnimationFrame(move);
      }
      else{
        setTimeout(() => {
          cancelAnimationFrame(animationFrame);
        }, 2000);
      }
      
      
  
      window.addEventListener("resize", updateDimensionsAndInitialize);
  
      return () => {
        if (animate) {
            cancelAnimationFrame(animationFrame);
        }
        window.removeEventListener("resize", updateDimensionsAndInitialize);
      };
    }, [speed, name, animate]); // Depend only on speed if it can change
  
    return (
      <div
        ref={containerRef}
        style={{
          position: "absolute",
          width: "100%",
          top: startY,
          height: height, // Example height
          //overflow: "hidden", // Crucial for hiding the off-screen image
        }}
      >
        <div
          ref={objectRef}
          style={{
            position: "relative",
            left: position,
            width: 'fit-content',
            
            transition: "none", // Avoid default CSS transitions
            whiteSpace: 'nowrap' // Prevents text/image wrapping which can affect width calculation
          }}
        >
          {children}
        </div>
      </div>
    );
  };

const GameScene = ({
  multiplier,
  speed = 200, // base px/sec
  isCrashed = false,
  crashMultiplier = 10,
  play = false
}) => {
  // State is only used to force re-render. All positions managed as refs for smoothness.
  const [, setRerender] = useState(0);

  // All logical positions are in refs for smooth animation:
  const bg1X = useRef(0);
  const bg2X = useRef(0);
  const bajajX = useRef(100);
  const [bajajOut, setBajajOut] = useState(false);
  const screenWidth = useRef(window.innerWidth);
  const [screen , setScreen] = useState({width: window.innerWidth, height: window.innerHeight});

  // For visibly rendering position, avoid triggering asynchronous React paint each frame:
  const [visible, setVisible] = useState({
    bg1X: 0,
    bg2X: 0,
    bajajX: 100,
  });

  const animFrameRef = useRef();
  const prevTime = useRef(performance.now());
  const prevMultiplier = useRef(multiplier);

  const calculateYPosition = (cdesktop , cmobile) => {
      let config = screen.width > 600 ? cdesktop : cmobile;
      let h = Math.min(screen.width, 600) * (500 / 800);
      let land_y =  screen.height * config;
      
      let tree_y = land_y - h;
      return tree_y;
    
  }

  // Parallax and bajaj logic (FLUSHED to the DOM, not state!):
  useEffect(() => {
    let running = true;
    function animate(now) {
      if (!running) return;
      const delta = Math.max(0.008, Math.min(0.045, (now - prevTime.current) / 1000 || 0.016)); // Clamp delta for rare tab visibility jumps

      if (isCrashed || (crashMultiplier && multiplier >= crashMultiplier)) {
        setBajajOut(true);
        // Parallax halts
        // Just move bajaj out at constant speed
        bajajX.current = bajajX.current + BAJAJ_CRASH_SPEED * delta;
      } else {
        // Parallax speed scales with multiplier (>1) -- logarithmic for fun effect
        const multFactor = Math.max(1, Math.log2(multiplier + 1));
        const effectiveSpeed1 = speed * PARALLAX_SPEED_BG1 * multFactor;
        const effectiveSpeed2 = speed * PARALLAX_SPEED_BG2 * multFactor;
        // Looping BGs using 3600px image size!
        bg1X.current = (bg1X.current - effectiveSpeed1 * delta) % BG_IMG_WIDTH;
        bg2X.current = (bg2X.current - effectiveSpeed2 * delta) % BG_IMG_WIDTH;
        
        // Ensure bg2X wraps properly for seamless looping
        if (bg2X.current > 0) {
          bg2X.current -= BG_IMG_WIDTH;
        }
        
        // Bajaj X proportional to mul but capped
        bajajX.current = clamp(
          100 + 250 * (multiplier - 1),
          100,
          900
        );
      }

      // Sync visible positions into component for rendering (small update once per frame)
      setVisible({
        bg1X: bg1X.current,
        bg2X: bg2X.current,
        bajajX: bajajX.current,
      });

      prevTime.current = now;
      prevMultiplier.current = multiplier;
      animFrameRef.current = requestAnimationFrame(animate);
    }
    animFrameRef.current = requestAnimationFrame(animate);
    return () => {
      running = false;
      animFrameRef.current && cancelAnimationFrame(animFrameRef.current);
    };
    // eslint-disable-next-line
  }, [speed, isCrashed, crashMultiplier, multiplier]);

  // Only reset bajaj on reset/crash end
  useEffect(() => {
    if (!isCrashed && multiplier <= 1) {
      bajajX.current = 100;
      setBajajOut(false);
      // Also reset backgrounds so next round always lines up visually nice
      bg1X.current = 0;
      bg2X.current = 0;
      setVisible({
        bg1X: bg1X.current,
        bg2X: bg2X.current,
        bajajX: bajajX.current,
      });
    }
    // Intentionally do NOT reset on every multiplier change!
    // eslint-disable-next-line
  }, [isCrashed, multiplier]);

  useEffect(() => {
    const update = () => setScreen({ width: window.innerWidth, height: window.innerHeight });
    window.addEventListener("resize", update);
    update();
    return () => window.removeEventListener("resize", update);
  }, []);


  // BG layers repeat, each image 3600px wide
  const renderParallaxBg = (img, offset, zIndex, top = 0, opacity = 1, style = {}) => {
    // Render three copies to ensure seamless looping
    return (
      <>
        <img
          src={img}
          alt=""
          style={{
            pointerEvents: "none",
            position: "absolute",
            left: offset,
            top,
            width: `${BG_IMG_WIDTH}px`,
            height: "100%",
            objectFit: "cover",
            zIndex,
            opacity,
            ...style
          }}
          draggable={false}
        />
        <img
          src={img}
          alt=""
          style={{
            pointerEvents: "none",
            position: "absolute",
            left: offset + BG_IMG_WIDTH,
            top,
            width: `${BG_IMG_WIDTH}px`,
            height: "100%",
            objectFit: "tile",
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "repeat",
            zIndex,
            opacity,
            ...style
          }}
          draggable={false}
        />
        <img
          src={img}
          alt=""
          style={{
            pointerEvents: "none",
            position: "absolute",
            left: offset + BG_IMG_WIDTH * 2,
            top,
            width: `${BG_IMG_WIDTH}px`,
            height: "100%",
            objectFit: "tile",
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "repeat",
            zIndex,
            opacity,
            ...style
          }}
          draggable={false}
        />
      </>
    );
  };

  return (
    <div
      style={{
        position: "absolute",
        overflow: "hidden",
        width: "100%",
        height: "100%",
        top: 0,
        left: 0,
        zIndex: 1,
        background: "linear-gradient(to bottom,rgb(44, 72, 187) 0%, #87CEEB 50%,rgb(99, 98, 97) 56%,rgb(72, 72, 71) 100%)",
        borderBottom: "2px solid #6B5344",
        userSelect: "none"
      }}
      className="game-scene-container"
    >
      {/* BG1: Distant Mountains and Clouds */}
      
      <MovingObject name = "cloud1" speed = {0.3} startX = {300} startY = {screen.height * 0.03} height = {100} initialDirection = {-1} canReset = {false} animate = {play}>
        <img src={defaultClouds} alt="Clouds" width={100} height={100} />
      </MovingObject>

      <MovingObject name = "cloud2" speed = {0.3} startX = {500} startY = {screen.height * 0.033} height = {200} initialDirection = {-1} canReset = {true} startOffScreen = {false} animate = {play}>
        <img src={defaultClouds2} alt="Clouds" width={200} height={200} />
      </MovingObject>

      <MovingObject name = "cloud3" speed = {0.2} startX = {200} startY = {screen.height * 0.05} height = {100} initialDirection = {-1} canReset = {true} startOffScreen = {false} animate = {play}>
        <img src={defaultClouds} alt="Clouds" width={100} height={100} />
      </MovingObject>


      <MovingObject name = "building" speed = {3} startX = {2400} startY = {calculateYPosition(0.6 , 0.5)} height = {300} initialDirection = {-1} canReset = {true} startOffScreen = {false} animate = {play}>
        <img src={building} alt="tree" width={600} height={600} />
      </MovingObject>


      <MovingObject name = "tree1" speed = {6} startX = {0} startY = {calculateYPosition(0.65 , 0.65)} height = {300} initialDirection = {-1} canReset = {false} startOffScreen = {false} animate = {play}>
        <img src={tree} alt="tree" width={600} height={300} />
      </MovingObject>
      <MovingObject name = "tree1" speed = {6} startX = {400} startY = {calculateYPosition(0.65 , 0.65)} height = {300} initialDirection = {-1} canReset = {false} startOffScreen = {false} animate = {play}>
        <img src={tree} alt="tree" width={600} height={300} />
      </MovingObject>
      <MovingObject name = "tree1" speed = {6} startX = {600} startY = {calculateYPosition(0.65 , 0.65)} height = {300} initialDirection = {-1} canReset = {false} startOffScreen = {false} animate = {play}>
        <img src={tree} alt="tree" width={600} height={300} />
      </MovingObject>
      <MovingObject name = "tree1" speed = {6} startX = {800} startY = {calculateYPosition(0.65 , 0.65)} height = {300} initialDirection = {-1} canReset = {false} startOffScreen = {false} animate = {play}>
        <img src={tree} alt="tree" width={600} height={300} />
      </MovingObject>
      <MovingObject name = "tree1" speed = {6} startX = {1000} startY = {calculateYPosition(0.65 , 0.65)} height = {300} initialDirection = {-1} canReset = {false} startOffScreen = {false} animate = {play}>
        <img src={tree} alt="tree" width={600} height={300} />
      </MovingObject>
      <MovingObject name = "tree1" speed = {6} startX = {1200} startY = {calculateYPosition(0.65 , 0.65)} height = {300} initialDirection = {-1} canReset = {false} startOffScreen = {false} animate = {play}>
        <img src={tree} alt="tree" width={600} height={300} />
      </MovingObject>
      <MovingObject name = "tree1" speed = {6} startX = {1400} startY = {calculateYPosition(0.65 , 0.65)} height = {300} initialDirection = {-1} canReset = {false} startOffScreen = {false} animate = {play}>
        <img src={tree} alt="tree" width={600} height={300} />
      </MovingObject>
      <MovingObject name = "tree1" speed = {6} startX = {1600} startY = {calculateYPosition(0.65 , 0.65)} height = {300} initialDirection = {-1} canReset = {false} startOffScreen = {false} animate = {play}>
        <img src={tree} alt="tree" width={600} height={300} />
      </MovingObject>


      <MovingObject name = "wall1" speed = {6} startX = {1800} startY = {calculateYPosition(0.78 , 0.68)} height = {100} initialDirection = {-1} canReset = {true} startOffScreen = {false} animate = {play}>
        <img src={wall1} alt="tree" width={500} height={100} />
      </MovingObject>
      <MovingObject name = "wall2" speed = {6} startX = {2000} startY = {calculateYPosition(0.78 , 0.68)} height = {100} initialDirection = {-1} canReset = {true} startOffScreen = {false} animate = {play}>
        <img src={wall2} alt="tree" width={500} height={100}  />
      </MovingObject>
      <MovingObject name = "wall2" speed = {6} startX = {2300} startY = {calculateYPosition(0.78 , 0.68)} height = {100} initialDirection = {-1} canReset = {true} startOffScreen = {false} animate = {play}>
        <img src={wall2} alt="tree" width={500} height={100}  />
      </MovingObject>
      <MovingObject name = "wall2" speed = {6} startX = {2500} startY = {calculateYPosition(0.78 , 0.68)} height = {100} initialDirection = {-1} canReset = {true} startOffScreen = {false} animate = {play}>
        <img src={wall2} alt="tree" width={500} height={100}  />
      </MovingObject>
      <MovingObject name = "wall2" speed = {6} startX = {2800} startY = {calculateYPosition(0.78 , 0.68)} height = {100} initialDirection = {-1} canReset = {true} startOffScreen = {false} animate = {play}>
        <img src={wall2} alt="tree" width={500} height={100}  />
      </MovingObject>
      <MovingObject name = "wall2" speed = {6} startX = {3000} startY = {calculateYPosition(0.78 , 0.68)} height = {100} initialDirection = {-1} canReset = {true} startOffScreen = {false} animate = {play}>
        <img src={wall2} alt="tree" width={500} height={100}  />
      </MovingObject>
      <MovingObject name = "wall2" speed = {6} startX = {3300} startY = {calculateYPosition(0.78, 0.68)} height = {100} initialDirection = {-1} canReset = {true} startOffScreen = {false} animate = {play}>
        <img src={wall2} alt="tree" width={500} height={100}  />
      </MovingObject>
      <MovingObject name = "wall1" speed = {6} startX = {3800} startY = {calculateYPosition(0.78, 0.68)} height = {100} initialDirection = {-1} canReset = {true} startOffScreen = {false} animate = {play}>
        <img src={wall2} alt="tree" width={500} height={100} />
      </MovingObject>


      <MovingObject name = "wall1" speed = {6} startX = {2600} startY = {calculateYPosition(0.78, 0.68)} height = {100} initialDirection = {-1} canReset = {true} startOffScreen = {false} animate = {play}>
        <div style={{
          background: 'rgba(167, 97, 31, 0.9)',
          padding: '10px 20px',
          height: '90px',
          borderRadius: '1px',
          fontSize: '20px',
          fontWeight: 'bold',
          marginTop: '20px',
          color: '#ccc',
          textAlign: 'center',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          Advertisemet Here
        </div>
      </MovingObject>

      

      
       


      <MovingObject name = "bjaj" speed = {12} startX = {screen.width > 800 ? screen.width * 0.4 : screen.width * 0.16} startY = {calculateYPosition(0.85 , 0.7)} height = {screen.height * 0.01} initialDirection = {1} canReset = {false} startOffScreen = {false} animate = {isCrashed}>
        <img src={bajajImg} alt="bajaj" width={Math.max(screen.height * 0.3, 300)} height={Math.max(screen.height * 0.3, 300)} />
      </MovingObject>

      




      {/*
      <div style={{ position: "absolute", width: "100%", height: "100%", zIndex: 1 }}>
        {renderParallaxBg(defaultClouds, visible.bg1X * 0.7, 2, 0, 0.92, { filter: "blur(0.5px)" })}
      </div>
      */}
      {/* BG2: Foreground */}
      {/*
      <div style={{ position: "absolute", width: "100%", height: "100%", zIndex: 4 }}>
        {renderParallaxBg(defaultForeground, visible.bg2X, 4, 148, 1)}
      </div>*}
      {/* Bajaj Character */}

      {/*
      <div
        style={{
          position: "absolute",
          left: visible.bajajX,
          bottom: 95,
          zIndex: 10,
          transition: isCrashed ? "none" : "left 0.18s cubic-bezier(0.17,0.77,0.49,1)",
          willChange: "left",
          display: "flex",
          alignItems: "flex-end"
        }}
      >
        <img
          src={bajajImg}
          alt="Bajaj"
          height={80}
          style={{
            userSelect: "none",
            pointerEvents: "none",
            width: "300px",
            height: "300px",
            filter: bajajOut ? "blur(2px) brightness(1.1)" : "none",
            transition: bajajOut ? "filter 0.15s" : undefined
          }}
          draggable={false}
        />
        
      </div>
      */}
      {/* FX: When crashed, draw (optionally) extra stuff (e.g. red overlay) */}

      {/*
      {(isCrashed || (crashMultiplier && multiplier >= crashMultiplier)) && (
        <div
          style={{
            position: "absolute",
            left: 0,
            top: 0,
            height: "100%",
            width: "100%",
            background: "rgba(255,32,32,0.14)",
            zIndex: 99,
            transition: "background 0.6s"
          }}
        />
      )}
      */}
   
    
    <style>{`
      @keyframes scroll {
        from {
          transform: translateX(0);
        }
        to {
          transform: translateX(-100%);
        }
      }
    `}</style>
    </div>
  );
};

export default GameScene;
